import { useGetAllArtworks } from "@/api/artwork/artwork";
import {
  useGetAllAttachments,
  type Attachment,
} from "@/api/attachment";
import { useGetAllMultiplePrints } from "@/api/multipleprint/multipleprint";
import {
  contentColumnsGridAttrs,
  selectionCellLayout,
  selectionColumnWidthPercent,
} from "@/lib/cms-puck/blocks/column-count";
import { normalizeGalleryColumns } from "@/lib/cms-puck/blocks/gallery";
import { useAuth } from "@/context/auth/AuthContext";
import { type LocaleDict, type SupportedLocale } from "@/i18n";
import { getDefaultAttachment } from "@/lib/image";
import type { Artwork, Multipleprint } from "@/lib/types";
import { useTranslation } from "react-i18next";
import {
  fetchCatalogItemsByIds,
  type CatalogPickItem,
} from "@/api/cms-puck/catalog-picker-api";
import {
  normalizeCaptionAlign,
  normalizeSelectionVisibility,
  resolveSelectionDisplayMode,
  resolveSelectionItems,
  resolveSelectionLayout,
  selectionIdsByType,
  type SelectionAlignment,
  type SelectionItem,
  type SelectionVisibility,
} from "@/lib/cms-puck/blocks/selection";
import { type BlockStyle } from "@/lib/cms-puck/block-style";
import { useBlockTextCss } from "@/hooks/use-block-style-css";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
// Compute-fields BO (mêmes composants que l'ancien CMS) → rendu de légende IDENTIQUE
// au front public (PuckSelectionCard). Réutilisés pour la parité exacte du canvas.
import { DimensionsFormatting } from "@/lib/compute-field/shared/dimensions";
import { InscriptionDosCF } from "@/lib/compute-field/shared/inscription-dos";
import { RelatedAuthorsListCF } from "@/lib/compute-field/shared/related-authors";
import { SignatureCF } from "@/lib/compute-field/shared/signature";
import { SupportTechniqueCF } from "@/lib/compute-field/shared/technique";
import { DateCF, MainTitleCF } from "@/lib/compute-field/shared/title";
import { computeFieldCollectorNameForOverview } from "@/lib/computeFields";
import { useQuery } from "@tanstack/react-query";

/**
 * Aperçu éditeur du bloc `ArtworkSelection` : résout les œuvres sélectionnées
 * par leurs ids (`useGetTinyArtworks`, scoping serveur) et affiche une grille /
 * un carrousel de vignettes + titre, façon front. L'éditeur n'est donc plus
 * aveugle. Fonctionnel, pas forcément fidèle au pixel près au front public (le
 * rendu réel y est fait à partir de `refs.artworks` résolu côté API).
 */
export const SELECTION_ITEMS_CLASS: Record<SelectionAlignment, string> = {
  center: "items-center",
  top: "items-start",
  grid: "items-start",
};
export const SELECTION_CAPTION_CLASS: Record<string, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

export function chunkRows<T>(arr: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < arr.length; i += size) rows.push(arr.slice(i, i + size));
  return rows;
}

/**
 * Aperçu éditeur du bloc `ItemSpotlight`, EN PARITÉ avec le front (légende selon
 * les bascules « Infos affichées ») :
 *  - Œuvre / Multiple → délègue à `ArtworkSelectionPreview` (1 item) qui rend la
 *    légende COMPLÈTE (auteurs, date, dimensions, technique, signature…) à
 *    l'identique du front, gating `visibility` ;
 *  - autres tables (écrits, photos, sculptures, tirages, expos, biblio…) →
 *    données de carte limitées (titre + n° d'enregistrement) → légende réduite
 *    aux infos disponibles (mêmes champs que le front pour ces types).
 * Vide → encart « aucun élément ».
 */
export function ItemSpotlightPreview(props: {
  item?: SelectionItem | null;
  captionAlign?: string;
  visibility?: SelectionVisibility;
  style?: BlockStyle;
}) {
  const item = props.item && props.item.id ? props.item : undefined;
  const isWork = item?.type === "artwork" || item?.type === "multipleprint";
  const visibility = normalizeSelectionVisibility(props.visibility);
  const captionTextCss = useBlockTextCss(props.style);
  const captionClass =
    SELECTION_CAPTION_CLASS[normalizeCaptionAlign(props.captionAlign)];
  // Œuvres/Multiples passent par ArtworkSelectionPreview (données complètes) →
  // ici on ne résout que les AUTRES types (carte simple titre + n°).
  const { data = [] } = useQuery({
    queryKey: ["catalog-spotlight-preview", item?.type, item?.id],
    queryFn: () =>
      item && !isWork
        ? fetchCatalogItemsByIds(item.type, [item.id])
        : Promise.resolve([] as CatalogPickItem[]),
    enabled: !!item && !isWork,
  });

  if (!item) {
    return (
      <div className="flex w-full items-center justify-center rounded border border-dashed border-slate-300 py-10 text-sm text-slate-400">
        {cmsNew(
          "cms.new.preview.spotlight.empty",
          "Aucun élément sélectionné.",
        )}
      </div>
    );
  }

  // Œuvre / Multiple : légende complète à l'identique du front (1 seul item),
  // sur 100% de la largeur du conteneur (mode spotlight).
  if (isWork) {
    return (
      <ArtworkSelectionPreview
        items={[item]}
        columns={2}
        alignment="grid"
        captionAlign={props.captionAlign}
        visibility={props.visibility}
        style={props.style}
        fullWidthItems
      />
    );
  }

  // Autres tables : carte simple + légende réduite (titre + n° selon les cases).
  const card = data[0];
  return (
    <div className="w-full">
      <div className="w-full">
        {card?.url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={card.url}
            alt={card.title ?? ""}
            className="h-auto w-full"
          />
        ) : (
          <div className="flex aspect-[4/3] w-full items-center justify-center bg-slate-100 text-xs text-slate-400">
            (pas d'image)
          </div>
        )}
        <div className={`pt-2 ${captionClass}`}>
          {visibility.registerNumber && card?.registrationNumber ? (
            <p className="text-xs cms-page-text-muted" style={captionTextCss}>
              {card.registrationNumber}
            </p>
          ) : null}
          {visibility.titles ? (
            <p className="font-semibold" style={captionTextCss}>
              {card?.title || "…"}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function ExhibitionListPreview({
  items,
  columns,
  captionAlign,
  style,
}: {
  items?: SelectionItem[];
  columns?: number;
  captionAlign?: string;
  style?: BlockStyle;
}) {
  const textCss = useBlockTextCss(style);
  const list = (items ?? []).filter((it) => it.type === "exhibition" && it.id);
  const cols = normalizeGalleryColumns(columns);
  const grid = contentColumnsGridAttrs(cols);
  const { data: resolved = [] } = useQuery({
    queryKey: ["preview-exhibitions", list.map((i) => i.id)],
    queryFn: () =>
      fetchCatalogItemsByIds(
        "exhibition",
        list.map((i) => i.id),
      ),
    enabled: list.length > 0,
  });
  const byId = new Map(resolved.map((r) => [r.id, r]));
  const align =
    captionAlign === "center"
      ? "text-center"
      : captionAlign === "right"
        ? "text-right"
        : "text-left";

  if (list.length === 0) {
    return (
      <div className="rounded border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">
        {cmsNew(
          "cms.new.preview.exhibitions.empty",
          "Sélectionne des expositions dans le panneau de droite",
        )}
      </div>
    );
  }

  return (
    <div className={grid.className} style={grid.style}>
      {list.map((it) => {
        const row = byId.get(it.id);
        return (
          <div
            key={it.id}
            className={`rounded border border-slate-200 p-4 ${align}`}
          >
            {row?.registrationNumber ? (
              <p className="text-xs text-slate-500" style={textCss}>
                {row.registrationNumber}
              </p>
            ) : null}
            <p className="font-semibold" style={textCss}>
              {row?.title ?? it.id}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Aperçu éditeur du bloc `ArtworkSelection` (œuvres + multiples), façon ancien CMS
 * front : images en TAILLE NATURELLE (ratio conservé, jamais de carré/recadrage),
 * largeur = pourcentage de colonne, alignement Centré/Haut/Grille. Le détail des 9
 * infos est rendu fidèlement côté FRONT ; l'aperçu BO montre image + titre + n°.
 */
/**
 * Carte d'œuvre d'EXEMPLE (aperçu bibliothèque uniquement) : image + légende
 * minimale. Permet de peupler l'aperçu de la Sélection sans œuvre réelle de
 * l'instance (cf. MediaPreview.sampleUrl, même principe).
 */
export type SampleArtworkCard = {
  imageUrl: string;
  title: string;
  author: string;
  date: string;
};

export function ArtworkSelectionPreview(props: {
  items?: SelectionItem[];
  artworkIds?: string;
  columns?: number;
  alignment?: string;
  displayMode?: string;
  align?: string;
  layout?: string;
  captionAlign?: string;
  visibility?: SelectionVisibility;
  style?: BlockStyle;
  /** Mode spotlight : l'unique item occupe 100% de la largeur (sinon % colonne). */
  fullWidthItems?: boolean;
  /**
   * Œuvres d'EXEMPLE affichées quand aucune œuvre réelle n'est sélectionnée —
   * APERÇU de la bibliothèque de modèles uniquement (jamais stockées en page).
   */
  sampleCards?: SampleArtworkCard[];
}) {
  const items = resolveSelectionItems(props);
  const { columns, alignment } = resolveSelectionLayout(props);
  const displayMode = resolveSelectionDisplayMode(props);
  const captionAlign = normalizeCaptionAlign(props.captionAlign);
  const visibility = normalizeSelectionVisibility(props.visibility);
  // Traitement du texte (onglet Design) appliqué INLINE sur chaque ligne de légende
  // pour BATTRE leurs classes Tailwind — parité avec le front (PuckSelectionCard).
  const captionTextCss = useBlockTextCss(props.style);
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const locale = language as SupportedLocale;
  const { artworkIds, multipleprintIds } = selectionIdsByType(items);
  // Cellule : pleine largeur en mobile, `widthPct` au-delà (parité front).
  const cell =
    props.fullWidthItems || displayMode === "carousel"
      ? { className: "w-full", style: undefined }
      : selectionCellLayout(selectionColumnWidthPercent(columns));
  // Items « image » (médias libres) : résolus depuis la médiathèque du CMS
  // (même scope que le picker — `docId = CMS.id`).
  const { instanceData: selInstanceData } = useAuth();
  const selCmsId = selInstanceData?.CMS?.id;
  const { data: selLibraryMedia = [] } = useGetAllAttachments({
    where: selCmsId ? { docId: selCmsId } : { docId: "__none__" },
    order: { order: "asc" },
  });
  const mediaById = new Map<string, Attachment>(
    (selLibraryMedia as Attachment[]).map((att) => [att.id, att]),
  );

  // Données COMPLÈTES par ids (auteurs, date, dimensions, technique, signature…) →
  // l'aperçu affiche les MÊMES infos que le front selon les bascules « Infos affichées ».
  const { data: artworks = [] } = useGetAllArtworks({
    where: { id: { in: artworkIds.length ? artworkIds : ["__none__"] } },
    order: [],
    take: artworkIds.length || 1,
    skip: 0,
  });
  const { data: multipleprints = [] } = useGetAllMultiplePrints({
    where: {
      id: { in: multipleprintIds.length ? multipleprintIds : ["__none__"] },
    },
    order: [],
    take: multipleprintIds.length || 1,
    skip: 0,
  });
  const artById = new Map<string, Artwork>(artworks.map((a) => [a.id, a]));
  const mpById = new Map<string, Multipleprint>(
    multipleprints.map((m) => [m.id, m]),
  );

  if (items.length === 0) {
    // Aperçu bibliothèque : œuvres d'exemple (grille représentative) si fournies.
    if (props.sampleCards && props.sampleCards.length > 0) {
      return (
        <div className="w-full">
          <div className="flex flex-wrap gap-8">
            {props.sampleCards.map((card, i) => (
              <div key={i} className={cell.className} style={cell.style}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={card.imageUrl}
                  alt={card.title}
                  className="h-auto w-full object-contain"
                />
                <div className={`pt-2 ${SELECTION_CAPTION_CLASS[captionAlign]}`}>
                  {visibility.titles && (
                    <p className="font-semibold" style={captionTextCss}>
                      {card.title}
                    </p>
                  )}
                  {visibility.authors && card.author && (
                    <p
                      className="text-sm cms-page-text-muted"
                      style={captionTextCss}
                    >
                      {card.author}
                    </p>
                  )}
                  {visibility.dates && card.date && (
                    <p
                      className="text-xs cms-page-text-muted"
                      style={captionTextCss}
                    >
                      {card.date}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="w-full">
        <div className="rounded border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">
          {cmsNew(
            "cms.new.preview.artworkSelection.empty",
            "Aucune œuvre sélectionnée",
          )}
        </div>
      </div>
    );
  }

  // Normalise une œuvre / un multiple en données de légende. Mapping ALIGNÉ sur le
  // résolveur public (PublicArtworkCard) et la SelectionCaption BO.
  type CaptionData = {
    title: string;
    noTitle: boolean;
    unknownTitle: boolean;
    authors: Artwork["relatedAuthor"];
    date: string;
    dateUnknown: boolean;
    dateUncertain?: boolean;
    dimensions: Artwork["oeuvresDescriptionDimensionsOeuvre"];
    dimensionsUnknown: boolean;
    technique: LocaleDict<string>;
    support: LocaleDict<string>;
    doubleFaced: boolean;
    signatureLocalisation: LocaleDict<string>;
    signatureTranscription: string;
    backText: LocaleDict<string>;
    registrationNumber: string;
    collection: string;
    imageUrl?: string;
  };

  const captionDataFor = (it: SelectionItem): CaptionData | null => {
    if (it.type === "artwork") {
      const a = artById.get(it.id);
      if (!a) return null;
      const att = a.attachments?.length
        ? getDefaultAttachment(a.attachments, "artwork")
        : null;
      return {
        title:
          a.oeuvresDescriptionTitreMultilingual?.[locale]?.trim() ||
          a.oeuvresDescriptionTitre,
        noTitle: a.oeuvresDescriptionTitreSansTitre,
        unknownTitle: a.oeuvresDescriptionTitreInconnu,
        authors: a.relatedAuthor ?? [],
        date: a.oeuvresDescriptionDateDeCreation,
        dateUnknown: a.oeuvresDescriptionDateDeCreationInconnue,
        dateUncertain: a.oeuvresDescriptionDateDeCreationIncertaine,
        dimensions: a.oeuvresDescriptionDimensionsOeuvre,
        dimensionsUnknown: a.oeuvresDescriptionDimensionsOeuvreInconnues,
        technique: a.oeuvresDescriptionTechnique ?? {},
        support: a.oeuvresDescriptionSupport ?? {},
        doubleFaced: a.oeuvresDescriptionOeuvreDoubleFace,
        signatureLocalisation: a.oeuvresDescriptionSignatureLocalisation ?? {},
        signatureTranscription:
          a.oeuvresDescriptionSignatureTranscription ?? "",
        backText: a.oeuvresDescriptionInscriptionsDosMultilingual ?? {},
        registrationNumber: a.oeuvresDescriptionNumeroEnregistrement ?? "",
        collection: a.oeuvresLocalisationHistoriqueCollection
          ? computeFieldCollectorNameForOverview(
              a.oeuvresLocalisationHistoriqueCollection,
              { language: locale, translation: t },
            )
          : (a.oeuvresLocalisationHistoriqueCollectionCatalogue ?? ""),
        imageUrl: att?.largePublicUrl ?? att?.mediumPublicUrl ?? att?.publicUrl,
      };
    }
    const m = mpById.get(it.id);
    if (!m) return null;
    const att = m.attachments?.length
      ? getDefaultAttachment(m.attachments, "multipleprint")
      : null;
    return {
      title: m.estampesMultiplesDescriptionTitre,
      noTitle: m.estampesMultiplesDescriptionTitreSansTitre,
      unknownTitle: m.estampesMultiplesDescriptionTitreInconnu,
      authors: m.relatedAuthor ?? [],
      date: m.estampesMultiplesDescriptionDateDeCreation,
      dateUnknown: m.estampesMultiplesDescriptionDateDeCreationInconnue,
      dateUncertain: m.estampesMultiplesDescriptionDateDeCreationIncertaine,
      dimensions: m.estampesMultiplesDescriptionDimensionsOeuvre,
      dimensionsUnknown:
        m.estampesMultiplesDescriptionDimensionsOeuvreInconnues,
      technique: m.estampesMultiplesDescriptionTechnique ?? {},
      support: m.estampesMultiplesDescriptionSupportMateriau ?? {},
      doubleFaced: m.estampesMultiplesDescriptionOeuvreDoubleFace,
      signatureLocalisation:
        m.estampesMultiplesDescriptionSignatureLocalisation ?? {},
      signatureTranscription:
        m.estampesMultiplesDescriptionSignatureTranscription ?? "",
      backText: m.estampesMultiplesDescriptionInscriptionsDos ?? {},
      registrationNumber:
        m.estampesMultiplesDescriptionNumeroEnregistrement ?? "",
      collection: "",
      imageUrl: att?.largePublicUrl ?? att?.mediumPublicUrl ?? att?.publicUrl,
    };
  };

  // Carte : image en taille naturelle + légende rendue À L'IDENTIQUE du front
  // (PuckSelectionCard) : même ORDRE, mêmes CLASSES, mêmes compute-fields, gating
  // « Infos affichées ». Données absentes (chargement / supprimé) → image seule.
  const Card = (it: SelectionItem) => {
    // Item « image » : média libre — image seule + légende custom éventuelle
    // (bascule « champ personnalisé »). Parité front (SelectionImageItem).
    if (it.type === "image") {
      const att = mediaById.get(it.id);
      const url = att?.mediumPublicUrl ?? att?.publicUrl ?? att?.largePublicUrl;
      return (
        <div key={`${it.type}:${it.id}`} className={cell.className} style={cell.style}>
          {url ? (
            <img
              src={url}
              alt={cmsNew("cms.new.auto.alt.d41d8c", "")}
              className="h-auto w-full object-contain"
            />
          ) : (
            <div className="flex aspect-[4/3] w-full items-center justify-center bg-slate-100 px-2 text-center text-[11px] text-slate-400">
              {att
                ? cmsNew(
                    "cms.new.preview.media.videoEmbed",
                    "Média (vidéo / embed)",
                  )
                : cmsNew("cms.new.preview.media.notFound", "Média introuvable")}
            </div>
          )}
          {visibility.customField && it.caption ? (
            <div className={`pt-2 ${SELECTION_CAPTION_CLASS[captionAlign]}`}>
              <div
                className="cms-html-content text-xs cms-page-text-muted max-w-none"
                style={captionTextCss}
                dangerouslySetInnerHTML={{ __html: it.caption }}
              />
            </div>
          ) : null}
        </div>
      );
    }
    const c = captionDataFor(it);
    const showTechnique =
      !!c &&
      visibility.techniqueAndSupport &&
      (!!c.technique?.[locale] || !!c.support?.[locale] || c.doubleFaced);
    const showSignature =
      !!c &&
      visibility.signature &&
      (!!c.signatureLocalisation?.[locale] || !!c.signatureTranscription);
    return (
      <div key={`${it.type}:${it.id}`} className={cell.className} style={cell.style}>
        {c?.imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={c.imageUrl}
            alt={c.title}
            className="h-auto w-full object-contain"
          />
        ) : (
          <div className="flex aspect-[4/3] w-full items-center justify-center bg-slate-100 px-2 text-center text-[11px] text-slate-400">
            {c?.title ?? it.id}
          </div>
        )}
        {c && (
          <div className={`pt-2 ${SELECTION_CAPTION_CLASS[captionAlign]}`}>
            {visibility.registerNumber && c.registrationNumber && (
              <p className="text-xs cms-page-text-muted" style={captionTextCss}>
                {c.registrationNumber}
              </p>
            )}
            {visibility.titles && (
              <p className="font-semibold" style={captionTextCss}>
                <MainTitleCF
                  input={{
                    title: c.title,
                    titleState: { none: c.noTitle, unknown: c.unknownTitle },
                  }}
                  language={locale}
                />
              </p>
            )}
            {visibility.authors && c.authors.length > 0 && (
              <p className="text-sm cms-page-text-muted" style={captionTextCss}>
                <RelatedAuthorsListCF
                  input={{
                    relatedAuthors: c.authors,
                    attributedTo: false,
                    from: false,
                    tasteOf: false,
                    entourageOf: false,
                    studioOf: false,
                  }}
                  language={locale}
                />
              </p>
            )}
            {visibility.dates && (
              <p
                className="text-base cms-page-text-muted"
                style={captionTextCss}
              >
                <DateCF
                  input={{
                    date: c.date,
                    unknownDate: c.dateUnknown,
                    uncertainDate: c.dateUncertain,
                  }}
                  language={locale}
                />
              </p>
            )}
            {visibility.dimensions && c.dimensions && (
              <p className="text-xs cms-page-text-muted" style={captionTextCss}>
                <DimensionsFormatting
                  input={{
                    ...c.dimensions,
                    unknownDimensions: c.dimensionsUnknown,
                  }}
                  language={locale}
                />
              </p>
            )}
            {showTechnique && (
              <p className="text-xs cms-page-text-muted" style={captionTextCss}>
                <SupportTechniqueCF
                  input={{
                    technique: c.technique,
                    support: c.support,
                    isDoubleSided: c.doubleFaced,
                  }}
                  language={locale}
                />
              </p>
            )}
            {showSignature && (
              <p className="text-xs cms-page-text-muted" style={captionTextCss}>
                <SignatureCF
                  input={{
                    location: c.signatureLocalisation,
                    transcription: c.signatureTranscription,
                  }}
                  language={locale}
                />
              </p>
            )}
            {visibility.backText && c.backText?.[locale] && (
              <p className="text-xs cms-page-text-muted" style={captionTextCss}>
                <InscriptionDosCF
                  input={{
                    inscription: c.backText,
                    published: { [locale]: true } as LocaleDict<boolean>,
                  }}
                  language={locale}
                />
              </p>
            )}
            {visibility.currentCollection && c.collection && (
              <p className="text-xs cms-page-text-muted" style={captionTextCss}>
                {c.collection}
              </p>
            )}
            {/* Champ personnalisé : légende custom par item (parité front). */}
            {visibility.customField && it.caption && (
              <div
                className="cms-html-content text-xs cms-page-text-muted max-w-none"
                style={captionTextCss}
                dangerouslySetInnerHTML={{ __html: it.caption }}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      {displayMode === "carousel" ? (
        <div className="flex snap-x snap-mandatory gap-6 overflow-x-auto pb-2">
          {items.map((it) => (
            <div
              key={`${it.type}:${it.id}`}
              className="w-full min-w-full shrink-0 snap-center"
            >
              {Card(it)}
            </div>
          ))}
        </div>
      ) : alignment === "grid" ? (
        // Grille : flux qui passe à la ligne (items en taille naturelle).
        <div className="flex flex-wrap gap-8">{items.map(Card)}</div>
      ) : (
        // Lignes de N, alignées verticalement au centre (center) ou en haut (top).
        <div className="flex flex-col gap-8">
          {chunkRows(items, columns).map((row, i) => (
            <div
              key={i}
              className={`flex flex-wrap gap-8 md:flex-nowrap ${SELECTION_ITEMS_CLASS[alignment]}`}
            >
              {row.map(Card)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
